import React, { memo, useMemo } from 'react';

import { Stack } from '@atlaskit/primitives/compiled';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	type GlobalShortcutsProps,
	type ProductLink,
	ShortcutMenuItem,
} from '@atlassian/global-shortcuts';

import type { MappedGlobalShortcutsItem } from './types';

export interface ShortcutsMenuProps extends Pick<
	GlobalShortcutsProps,
	'shortcutItems' | 'product' | 'openInSameTab'
> {
	invitedAppProductLinks: Record<string, ProductLink[]>;
}

const ShortcutsMenuComponent = ({
	shortcutItems = [],
	product,
	openInSameTab,
	invitedAppProductLinks,
}: ShortcutsMenuProps): React.JSX.Element => {
	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems
			.map((item) => {
				if (item.type === 'link' && item.apsKey) {
					const key = item.apsKey;
					const filteredProductLinks = invitedAppProductLinks[item.apsKey];
					// By the time we got here, we've already decided we're eligible etc.,
					// During SSR, we don't have the product links yet
					// so we return the item without URL to ensure rendering the shortcut
					// After the product links are loaded, the shortcut will be updated with the URL
					if (!filteredProductLinks) {
						return {
							...item,
							key,
						};
					}

					// we only want to take single link items as we are not supporting expand menus
					if (filteredProductLinks.length === 1 && !item.url) {
						return {
							...item,
							key,
							url: filteredProductLinks[0].url,
						};
					}
				}

				return null;
			})
			.filter(Boolean) as Array<MappedGlobalShortcutsItem>;
	}, [shortcutItems, invitedAppProductLinks]) as Array<MappedGlobalShortcutsItem>;

	return (
		<Stack testId="shortcuts-menu">
			<MenuList>
				{mappedShortcutItems.map((shortcut) => (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						product={product}
						openInSameTab={openInSameTab}
					/>
				))}
			</MenuList>
		</Stack>
	);
};

export const ShortcutsMenu: React.MemoExoticComponent<({ shortcutItems, product, openInSameTab, invitedAppProductLinks, }: ShortcutsMenuProps) => React.JSX.Element> = memo(ShortcutsMenuComponent);
